// Zentrale Konstanten der Anwendung.
// Werte, die an mehreren Stellen gebraucht werden, stehen hier genau einmal –
// bitte nicht in einzelnen Seiten/Modals erneut definieren.

// ---------------------------------------------------------------------------
// Rollen-IDs (müssen mit der Datenbank übereinstimmen)
// ---------------------------------------------------------------------------
export const ROLLE_ADMINISTRATOR = 1;
export const ROLLE_SCHIESSMEISTER = 2;
export const ROLLE_WETTKAMPFMANAGER = 3;

// ---------------------------------------------------------------------------
// Saison-Status
// 21–23 sind Zwischenschritte der Einteilungsphase (z. B. Autoeinteilung erledigt).
// ---------------------------------------------------------------------------
export const SAISON_STATUS_PLANUNG = 0;
export const SAISON_STATUS_MELDUNG = 10;
export const SAISON_STATUS_EINTEILUNG = 20;
export const SAISON_STATUS_AKTIV = 30;
export const SAISON_STATUS_ABGESCHLOSSEN = 40;

export const SAISON_STATUS_LABELS = {
  0: 'Planung',
  10: 'Meldung',
  20: 'Einteilung',
  21: 'Einteilung',
  22: 'Einteilung',
  23: 'Einteilung',
  30: 'Aktiv',
  40: 'Abgeschlossen',
};

export const SAISON_STATUS_BADGE_CLASS = {
  0: 'badge-gray',
  10: 'badge-blue',
  20: 'badge-amber',
  21: 'badge-amber',
  22: 'badge-amber',
  23: 'badge-amber',
  30: 'badge-green',
  40: 'badge-gray',
};

// ---------------------------------------------------------------------------
// Wettkampf-Status
// ---------------------------------------------------------------------------
export const WETTKAMPF_STATUS_LABELS = {
  offen: 'Offen',
  ueberfaellig: 'Überfällig',
  verschoben: 'Verschoben',
  unterschrift: 'Unterschrift ausstehend',
  abgeschlossen: 'Abgeschlossen',
  gestrichen: 'Gestrichen',
};

export const WETTKAMPF_STATUS_BADGE_CLASS = {
  offen: 'badge-gray',
  ueberfaellig: 'badge-red',
  verschoben: 'badge-amber',
  unterschrift: 'badge-amber',
  abgeschlossen: 'badge-green',
  gestrichen: 'badge-gray',
};

// ---------------------------------------------------------------------------
// Mannschafts-Status
// ---------------------------------------------------------------------------
export const MANNSCHAFT_STATUS_LABELS = {
  vorgeschlagen: 'Vorgeschlagen',
  bearbeitet: 'Bearbeitet',
  gemeldet: 'Gemeldet',
  eingruppiert: 'Eingruppiert',
  abgemeldet: 'Abgemeldet',
};

// Gleiche Farblogik wie WETTKAMPF_STATUS_BADGE_CLASS:
// grau = neutral/inaktiv, amber = Aktion nötig, blau = Zwischenschritt, grün = erledigt.
export const MANNSCHAFT_STATUS_BADGE_CLASS = {
  vorgeschlagen: 'badge-gray',
  bearbeitet: 'badge-amber',
  gemeldet: 'badge-blue',
  eingruppiert: 'badge-green',
  abgemeldet: 'badge-gray',
};

// Mannschaften, für die eine Meldung abgeschickt wurde – inklusive der wieder
// abgemeldeten. Für "wie viele Mannschaften hat der Verein gemeldet?".
export const GEMELDET_STATUSES = new Set(['gemeldet', 'eingruppiert', 'abgemeldet']);

// Mannschaften mit aktiver Meldung: abgemeldete und noch nicht abgeschickte bleiben
// außen vor. Das sind gleichzeitig die Mannschaften, die eingeteilt werden dürfen.
export const AKTIV_GEMELDETE_STATUSES = new Set(['gemeldet', 'eingruppiert']);

// Die Stand-Terminplanung kennt nur Muster für 6er- und 8er-Teilungen – einer
// Klasse können daher höchstens 8 Mannschaften zugeordnet werden.
export const MAX_MANNSCHAFTEN_PRO_KLASSE = 8;

// Kartenfarben je Klasse (z. B. Mannschaftskarte in der Mannschaftszuordnung).
// Zyklisch verwendet, falls mehr Klassen als Farben vorhanden sind.
export const KLASSEN_FARBEN = [
  '#2563eb', // blau
  '#dc2626', // rot
  '#16a34a', // grün
  '#d97706', // amber
  '#9333ea', // lila
  '#0891b2', // cyan
  '#db2777', // pink
  '#65a30d', // lime
  '#4f46e5', // indigo
  '#ea580c', // orange
];

// ---------------------------------------------------------------------------
// Wochentage (Schlüssel 1 = Montag .. 7 = Sonntag, wie in der Datenbank)
// ---------------------------------------------------------------------------
export const WETTKAMPFTAG_LABELS = { 1: 'Mo', 2: 'Di', 3: 'Mi', 4: 'Do', 5: 'Fr', 6: 'Sa', 7: 'So' };

export const WETTKAMPFTAG_LABELS_LANG = {
  1: 'Montag', 2: 'Dienstag', 3: 'Mittwoch', 4: 'Donnerstag',
  5: 'Freitag', 6: 'Samstag', 7: 'Sonntag',
};

// Gleiche Namen als Liste – Index 0 = Montag (für <select>-Listen).
export const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

// ---------------------------------------------------------------------------
// Wettkampfplan: feste Paarungen je Mannschaftszahl.
// Aufbau: WETTKAMPFPLAN[anzahlMannschaften][runde] = [[heim, gast], ...]
// Die ersten HINRUNDE_COUNT Runden sind die Hinrunde, danach folgt die Rückrunde.
// ---------------------------------------------------------------------------
export const WETTKAMPFPLAN = {
  8: [
    [[1,5],[2,6],[3,7],[4,8]],   // Runde 1
    [[8,2],[6,1],[7,4],[5,3]],   // Runde 2
    [[3,8],[1,7],[4,6],[2,5]],   // Runde 3
    [[5,4],[8,1],[6,3],[7,2]],   // Runde 4
    [[6,8],[7,5],[3,4],[1,2]],   // Runde 5
    [[8,7],[4,1],[2,3],[5,6]],   // Runde 6
    [[5,8],[3,1],[2,4],[6,7]],   // Runde 7
    [[5,1],[6,2],[7,3],[8,4]],   // Runde 8
    [[2,8],[1,6],[4,7],[3,5]],   // Runde 9
    [[8,3],[7,1],[6,4],[5,2]],   // Runde 10
    [[4,5],[1,8],[3,6],[2,7]],   // Runde 11
    [[8,6],[5,7],[4,3],[2,1]],   // Runde 12
    [[7,8],[1,4],[3,2],[6,5]],   // Runde 13
    [[8,5],[1,3],[4,2],[7,6]],   // Runde 14
  ],
  6: [
    [[1,4],[2,5],[3,6]],   // Runde 1
    [[5,1],[4,3],[6,2]],   // Runde 2
    [[2,4],[3,1],[5,6]],   // Runde 3
    [[1,2],[3,5],[6,4]],   // Runde 4
    [[1,6],[2,3],[4,5]],   // Runde 5
    [[4,1],[5,2],[6,3]],   // Runde 6
    [[1,5],[3,4],[2,6]],   // Runde 7
    [[4,2],[1,3],[6,5]],   // Runde 8
    [[2,1],[5,3],[4,6]],   // Runde 9
    [[6,1],[3,2],[5,4]],   // Runde 10
  ],
};

// Anzahl der Runden, die zur Hinrunde gehören (je Mannschaftszahl).
export const HINRUNDE_COUNT = { 8: 7, 6: 5 };
